import axios from "axios";
import { useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { AiFillEye, AiFillEyeInvisible } from "react-icons/ai";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { setToken, setUser } from "../../redux/userSlice.js";
import "../formStyles.css";

const LoginPage = () => {
  const [data, setData] = useState({
    phone: "",
    password: "",
  });
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [phoneStatus, setPhoneStatus] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  const { t } = useTranslation();

  const handleOnChange = (e) => {
    const { name, value } = e.target;
    setData((prev) => ({ ...prev, [name]: value }));

    if (name === "phone") validatePhone(value);

    // Reset field-specific error if it becomes valid
    if (formErrors[name]) setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validatePhone = (phone) => {
    if (!phone) {
      setPhoneStatus("");
      return;
    }
    const hk = /^\+?852[- ]?\d{8}$/;
    const local = /^\d{8}$/;
    setPhoneStatus(
      hk.test(phone) || local.test(phone)
        ? ""
        : t("invalidHKPhone", { defaultValue: "Invalid Hong Kong phone" })
    );
  };

  const validateForm = () => {
    const errors = {};
    let isValid = true;

    if (!data.phone) {
      errors.phone = t("phoneRequired", { defaultValue: "Phone is required" });
      isValid = false;
    }
    if (!data.password) {
      errors.password = t("passwordRequired", {
        defaultValue: "Password is required",
      });
      isValid = false;
    }
    if (phoneStatus) {
      errors.phone = phoneStatus;
      isValid = false;
    }

    setFormErrors(errors);
    if (!isValid) {
      toast.error(Object.values(errors).join(" ") || t("formInvalid"));
    }
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!validateForm()) return;

    const URL = `${process.env.REACT_APP_BACKEND_URL}/auth.php`;
    try {
      const response = await axios.post(
        URL,
        { phone: data.phone, password: data.password },
        { withCredentials: true }
      );
      toast.success(t(response?.data?.message));
      if (response.data.success) {
        dispatch(setToken(response.data.data.token));
        dispatch(setUser(response.data.data.user));
        setData({ phone: "", password: "" });
        navigate("/");
      }
    } catch (error) {
      console.error(error);
      toast.error(
        error?.response?.data?.error ||
          error?.response?.data?.message ||
          t("loginError")
      );
    }
  };

  const getInputClass = (field) =>
    formErrors[field] ? "input-error" : "input-default";

  return (
    <div className="form-container">
      <div>
        <h2>{t("welcome")}</h2>

        <form onSubmit={handleSubmit} className="grid gap-4">
          <div>
            <label htmlFor="phone">
              {t("phone", { defaultValue: "Phone" })}
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              placeholder={t("phonePlaceholder", {
                defaultValue: "85212345678 or 12345678",
              })}
              className={getInputClass("phone")}
              value={data.phone}
              onChange={handleOnChange}
              required
            />
            {formErrors.phone && (
              <div className="error-message">{formErrors.phone}</div>
            )}
            {phoneStatus && !formErrors.phone && (
              <div className="error-message">{phoneStatus}</div>
            )}
          </div>

          <div className="flex flex-col gap-1 relative">
            <label htmlFor="password">
              {t("password", { defaultValue: "Password" })}
            </label>
            <div
              className="cursor-pointer absolute right-2 top-9"
              onClick={() => setShowPassword((s) => !s)}
              title={
                showPassword
                  ? t("hide", { defaultValue: "Hide" })
                  : t("show", { defaultValue: "Show" })
              }
            >
              {showPassword ? (
                <AiFillEyeInvisible size={20} />
              ) : (
                <AiFillEye size={20} />
              )}
            </div>
            <input
              type={showPassword ? "text" : "password"}
              id="password"
              name="password"
              className={getInputClass("password")}
              value={data.password}
              onChange={handleOnChange}
              required
            />
            {formErrors.password && (
              <div className="error-message">{formErrors.password}</div>
            )}
          </div>

          <button type="submit">{t("login")}</button>
        </form>

        <div className="pt-10">
          {t("dontHaveAccount")}{" "}
          <Link
            to="/register"
            className="bg-yellow-400 p-2 hover:bg-yellow-200"
          >
            {t("register.title")}
          </Link>
        </div>
        <div className="p-10">
          <button onClick={() => navigate("/forget-password")}>
            {t("forgetPassword.title")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
